/** Fields needed to build manual-food/edit payload (backend scales by count / original_count). */
export interface FoodItemEditSource {
  name: string;
  count?: number;
  servings?: number;
  weight?: number;
  serving_description?: string;
  unit_label?: string;
  predicted_count?: number;
  predicted_serving_size_g?: number;
  predicted_serving_description?: string;
  predicted_unit_label?: string;
}

const parseLeadingCount = (desc?: string): number | undefined => {
  if (!desc) return undefined;
  const match = desc.match(/^(\d+(?:\.\d+)?)\s/);
  if (!match) return undefined;
  const parsed = parseFloat(match[1]);
  return parsed > 0 ? parsed : undefined;
};

export const stripCountFromServingDescription = (desc?: string): string =>
  (desc ?? "")
    .replace(/^\d+(\.\d+)?x?\s*/i, "")
    .replace(/^\d+(\.\d+)?\s*/, "")
    .trim();

/**
 * Original count from first predict/scan — never the user's current edited count.
 * Backend: new_grams = (serving_size_g / original_count) × count
 */
export const getStoredOriginalCount = (item: FoodItemEditSource): number =>
  item.predicted_count ??
  parseLeadingCount(item.predicted_serving_description) ??
  parseLeadingCount(item.serving_description) ??
  (item.servings && item.servings > 0 ? item.servings : 1);

/** Total grams as originally stored from predict API — not recalculated on edit. */
export const getStoredOriginalServingSizeG = (item: FoodItemEditSource): number =>
  item.predicted_serving_size_g ?? (Number(item.weight) || 0);

export const getStoredUnitLabel = (item: FoodItemEditSource): string =>
  item.predicted_unit_label?.trim() ||
  item.unit_label?.trim() ||
  stripCountFromServingDescription(
    item.predicted_serving_description ?? item.serving_description
  ) ||
  "serving";

export const buildEditFoodPayload = (
  item: FoodItemEditSource,
  editedName: string,
  editedCount: number
) => {
  const unit = getStoredUnitLabel(item);

  return {
    name: editedName.trim(),
    count: editedCount,
    original_count: getStoredOriginalCount(item),
    serving_size_g: getStoredOriginalServingSizeG(item),
    unit_label: unit,
    serving_description: `${editedCount} ${unit}`,
  };
};
